const TICK_FONT = '12px sans-serif'
const TICK_FONT_SIZE_PX = 12
/** Max width used when wrapping category labels on the Y-axis (horizontal bars). */
export const CATEGORY_LABEL_WRAP_WIDTH = 120
export const TICK_LINE_HEIGHT = 16

export function measureChartTextWidth(text: string, font = TICK_FONT): number {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return text.length * 7
  context.font = font
  return context.measureText(text).width
}

export function wrapCategoryLabelLines(text: string, maxWidthPx: number, maxLines: number): string[] {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return [text]
  context.font = TICK_FONT

  if (context.measureText(text).width <= maxWidthPx) {
    return [text]
  }

  const words = text.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    if (context.measureText(testLine).width > maxWidthPx && currentLine) {
      if (lines.length + 1 >= maxLines) {
        let truncated = `${currentLine}...`
        while (context.measureText(truncated).width > maxWidthPx && truncated.length > 4) {
          truncated = `${truncated.slice(0, -4)}...`
        }
        lines.push(truncated)
        return lines
      }
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

/** Width for a category Y-axis (horizontal bars) from label text, including optional wrapping. */
export function calculateCategoryYAxisWidth(labels: string[], lineClampCategoryLabels?: number): number {
  if (lineClampCategoryLabels) {
    const maxLineWidth = Math.max(
      0,
      ...labels.flatMap((label) =>
        wrapCategoryLabelLines(label, CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels).map((line) =>
          measureChartTextWidth(line)
        )
      )
    )
    return Math.ceil(maxLineWidth + 12)
  }

  const maxLabelWidth = Math.max(0, ...labels.map((label) => measureChartTextWidth(label)))
  return Math.ceil(maxLabelWidth + 12)
}

/** Band size for an axis with -45° tick labels (category X on vertical bars, value X on horizontal bars). */
export function calculateAngledAxisBandSize(labels: string[], angleDegrees = 45): number {
  const maxLabelWidth = Math.max(0, ...labels.map((label) => measureChartTextWidth(label)))
  const radians = (angleDegrees * Math.PI) / 180
  const verticalExtent = maxLabelWidth * Math.sin(radians) + TICK_FONT_SIZE_PX * Math.cos(radians)
  return Math.ceil(verticalExtent + 12)
}

/** Band size for category X-axis when labels are rotated -90° (line-clamped vertical bars). */
export function calculateVerticalTickAxisBandSize(
  labels: string[],
  lineClampCategoryLabels?: number
): number {
  const maxLineWidth = lineClampCategoryLabels
    ? Math.max(
        0,
        ...labels.flatMap((label) =>
          wrapCategoryLabelLines(label, CATEGORY_LABEL_WRAP_WIDTH, lineClampCategoryLabels).map((line) =>
            measureChartTextWidth(line)
          )
        )
      )
    : Math.max(0, ...labels.map((label) => measureChartTextWidth(label)))

  return Math.ceil(maxLineWidth + 12)
}
